import { useMutation } from '@tanstack/react-query';
import { getCoreRowModel, TableOptions, useReactTable } from '@tanstack/react-table';
import { ComponentProps, ReactNode, useMemo } from 'react';

import { useSession } from '@/auth/useSession';
import { allCoachColumns, type CoachSessionsTableColumnId } from '@/components/session/sessions-table-columns';
import { BaseEntityTableGroupKey, EntityTable, EntityTableGroupBy } from '@/components/table/entity-table';
import { attendSession } from '@/lib/api-calls';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { useErrorToast } from '@/lib/error-handling';
import { CoachPortalSession } from '@airreading/common/data';

export type EntityTableProps = ComponentProps<typeof EntityTable>;

export interface CoachSessionsTableProps<GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey> {
  sessions: CoachPortalSession[];
  errorState?: ReactNode;
  isLoading?: boolean;
  hasError?: boolean;
  filters?: EntityTableProps['filters'];
  filterBarClassName?: EntityTableProps['filterBarClassName'];
  filterLeftClassName?: EntityTableProps['filterLeftClassName'];
  tableHookProps?: Omit<TableOptions<CoachPortalSession>, 'columns' | 'data' | 'getCoreRowModel'>;
  hideColumnIds?: Array<CoachSessionsTableColumnId>;
  tableTestId?: string;
  rowTestId?: (rowId: string, rowIndex: number) => string;
  cellTestId?: (rowId: string, columnId: string, rowIndex: number) => string | undefined;
  tablePrefix?: string;
  groupBy?: EntityTableGroupBy<CoachPortalSession, GroupKey>;
}

export const CoachSessionsTable = <GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey>({
  sessions,
  errorState,
  filters,
  filterBarClassName,
  filterLeftClassName,
  hasError,
  isLoading,
  tableHookProps,
  hideColumnIds = [],
  groupBy,
  tableTestId,
  rowTestId,
  cellTestId,
  tablePrefix,
}: CoachSessionsTableProps<GroupKey>) => {
  const { actingUser } = useSession();
  const { mutate: attendSessionMutate, error: attendSessionError } = useMutation({
    mutationFn: attendSession,
  });
  useErrorToast(attendSessionError, 'Unknown error: Failed to record that you joined this class.');

  const columns = useMemo(
    // column is kept if it does not have an id (cannot be hidden) or if it is not in the hideColumnIds
    () =>
      allCoachColumns({
        actingUser,
        onStartOrJoinSessionButtonClick: (row) => {
          attendSessionMutate({ id: row.id });
        },
        tablePrefix,
      }).filter((c) => !c.id || !(hideColumnIds as string[]).includes(c.id)),
    [hideColumnIds, actingUser, attendSessionMutate, tablePrefix],
  );

  const { rowSelection, onRowSelectionChange, getRowId } = useRowsBulkSelection<CoachPortalSession>({
    getRowId: (row) => row.id,
    rowIdToData: (id) => sessions.find((session) => session.id === id),
  });

  const table = useReactTable({
    data: sessions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    getRowId,
    onRowSelectionChange,
    ...tableHookProps,
    defaultColumn: {
      enableSorting: false,
      ...tableHookProps?.defaultColumn,
    },
    state: {
      rowSelection,
      ...tableHookProps?.state,
    },
  });

  return (
    <EntityTable
      table={table}
      emptyState={'No sessions found'}
      errorState={errorState}
      isLoading={isLoading}
      hasError={hasError}
      filters={filters}
      filterBarClassName={filterBarClassName}
      filterLeftClassName={filterLeftClassName}
      showPagination={true}
      data-testid={tableTestId}
      rowTestId={rowTestId}
      cellTestId={cellTestId}
      groupBy={groupBy}
    />
  );
};
